import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const AUTH_STORAGE_KEY = 'academy-auth';

// [중요] 저장된 refresh token이 서버에서 이미 폐기된 경우(세션 만료, 프로젝트 재연결,
// 토큰 로테이션 충돌 등), @supabase/supabase-js는 클라이언트가 만들어지자마자 저장된
// 세션을 복구(_recoverAndRefresh)하면서 백그라운드에서 refresh token 갱신을 자동 시도한다.
// 이때 서버가 "Invalid Refresh Token: Refresh Token Not Found"를 돌려주면, 그 예외가
// 라이브러리 내부(개발자가 잡을 수 없는) 자동갱신 루프에서 던져져 콘솔/오류 오버레이에
// 그대로 노출된다.
//
// 그래서 클라이언트의 첫 복구 시도 동안에는 저장소 읽기를 잠시 막아(빈 세션처럼 보이게)
// 자동 복구가 refresh token을 건드리지 못하게 하고, 그다음 우리가 직접 refreshSession()을
// try/catch로 감싸 안전하게 검증·복구한다. refresh token이 죽어 있으면 그때만 조용히
// 로컬 세션을 정리하고, 살아 있으면 정상 복원한다.
let authStorageReady = false;

const safeAuthStorage = {
  getItem: (key: string): string | null => {
    if (!authStorageReady) return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* noop */
    }
  },
  removeItem: (key: string): void => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  },
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: AUTH_STORAGE_KEY,
    storage: safeAuthStorage,
  },
});

function isDeadRefreshTokenMessage(message: string): boolean {
  return (
    message.includes('Invalid Refresh Token') ||
    message.includes('Refresh Token Not Found') ||
    message.includes('refresh_token_not_found')
  );
}

function isTransientNetworkMessage(message: string): boolean {
  return (
    message.includes('Failed to fetch') ||
    message.includes('Network request failed') ||
    message.includes('fetch failed') ||
    message.includes('Load failed') ||
    message.includes('network error')
  );
}

// 클라이언트 초기 복구가 끝날 때까지(첫 매크로태스크) 기다린 뒤 저장소 읽기를 허용하고,
// 저장된 세션을 우리가 직접 안전하게 검증·복구한다.
const authInitPromise: Promise<void> = new Promise<void>((resolve) => {
  setTimeout(async () => {
    authStorageReady = true;

    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    } catch {
      raw = null;
    }

    if (!raw) {
      resolve();
      return;
    }

    let storedSession: { refresh_token?: string } | null = null;
    try {
      storedSession = JSON.parse(raw);
    } catch {
      storedSession = null;
    }

    if (!storedSession?.refresh_token) {
      try {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
      } catch {
        /* noop */
      }
      resolve();
      return;
    }

    const refreshToken = storedSession.refresh_token;

    try {
      const { error } = await supabase.auth.refreshSession({ refresh_token: refreshToken });
      if (error && isDeadRefreshTokenMessage(error.message)) {
        try {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
        } catch {
          /* noop */
        }
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }
    } catch (err) {
      const message =
        typeof err === 'string' ? err : (err as { message?: string } | null)?.message || '';
      // 죽은 토큰이거나 예상치 못한 오류면 정리, 일시적 네트워크 오류면 세션 유지.
      if (isDeadRefreshTokenMessage(message) || !isTransientNetworkMessage(message)) {
        try {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
        } catch {
          /* noop */
        }
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }
    }

    resolve();
  }, 0);
});

// 앱이 로그인 상태를 판단하기 전에, 저장된 세션 복구가 끝났는지 기다릴 수 있게 해준다.
export function waitForAuthInit(): Promise<void> {
  return authInitPromise;
}

// [중요] 예전에는 "Refresh Token" 관련 에러가 한 번이라도 뜨면 곧바로
// signOut()으로 로컬 세션을 지워버렸어. 그런데 Supabase는 백그라운드에서
// 토큰을 주기적으로 자동 갱신하는데, 이때 네트워크가 잠깐 끊기거나 일시적
// 오류가 나도 같은 메시지가 뜰 수 있어. 그 결과 멀쩡한 로그인 세션이 조용히
// 로그아웃되고, 이후 모든 쿼리가 익명으로 실행돼서 RLS가 데이터를 전부
// 막아버렸어 (= 학생명단이 사라진 것처럼 보임).
//
// 그래서 이제 전역 핸들러는 "콘솔에 지저분한 에러가 뜨는 것만" 조용히
// 막아주고, 절대 세션을 강제로 지우지 않아. 세션 정리는 실제로 세션이
// 확인되지 않을 때(getSafeSession)만 신중하게 처리해.
if (typeof window !== 'undefined') {
  // 두 종류의 에러를 명확히 구분해.
  //
  // 1) "Refresh Token Not Found" / "Invalid Refresh Token"
  //    → 서버가 "이 refresh token은 이미 없어요(만료·폐기됨)"라고 명확히 답한
  //      진짜 세션 만료 신호. 이건 일시적 네트워크 문제와 달리 다시 시도해도
  //      절대 살아나지 않아. 이 경우엔 로컬 세션을 정리하고 로그인 화면으로
  //      보내서 새로 로그인(새 토큰 발급)하도록 유도해야 해.
  //
  // 2) "Failed to fetch" / "Network request failed" / "fetch failed" 등
  //    → 네트워크가 잠깐 끊긴 일시적 오류. 이건 세션을 지우면 안 되고,
  //      그냥 조용히 넘겨야 해 (다음 자동 갱신 때 다시 시도).
  const isRefreshTokenDead = (message: string) =>
    message.includes('Invalid Refresh Token') ||
    message.includes('Refresh Token Not Found');

  const isTransientNetworkError = (message: string) =>
    message.includes('Failed to fetch') ||
    message.includes('Network request failed') ||
    message.includes('fetch failed') ||
    message.includes('Load failed') ||
    message.includes('network error');

  // 중복 로그아웃 방지용 플래그
  let handledRefreshTokenDeath = false;

  const handleRejection = (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const message =
      typeof reason === 'string'
        ? reason
        : (reason as { message?: string } | null)?.message || '';

    if (isRefreshTokenDead(message)) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (handledRefreshTokenDeath) return;
      handledRefreshTokenDeath = true;

      // 진짜 세션 만료 → 로컬 세션만 정리 (서버에 로그아웃 요청은
      // 어차피 토큰이 없어서 실패할 테니 scope: 'local'로만).
      // onAuthStateChange가 SIGNED_OUT을 감지해서 로그인 화면으로 보내줘.
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      return;
    }

    if (isTransientNetworkError(message)) {
      // 일시적 네트워크 오류 → 콘솔 노이즈만 억제하고 세션은 유지.
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  window.addEventListener('unhandledrejection', handleRejection);

  window.addEventListener('error', (event) => {
    const msg = event?.message || '';
    if (isRefreshTokenDead(msg)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!handledRefreshTokenDeath) {
        handledRefreshTokenDeath = true;
        supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }
    } else if (isTransientNetworkError(msg)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
}

// getSafeSession()은 현재 세션을 안전하게 가져와.
// 1) 우선 getUser()로 서버에 토큰이 실제로 유효한지 검증 (로컬 캐시가 아니라 서버 확인)
// 2) 토큰이 만료됐으면 refreshSession()으로 갱신 시도
// 3) 갱신까지 실패하면 그때만 로컬 세션을 정리하고 null 반환
export async function getSafeSession() {
  // 저장된 세션 복구(토큰 검증)가 끝난 뒤에 세션을 판단해야, 복구 중인 세션을
  // '로그아웃'으로 잘못 해석하지 않는다.
  await authInitPromise.catch(() => {});

  try {
    // 서버 검증: getUser()는 로컬에 저장된 토큰이 아니라
    // 실제 서버에 물어봐서 "지금도 유효한 로그인인지" 확인해.
    // 로컬 세션은 남아있어도 토큰이 만료됐으면 여기서 걸러져.
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (!userError && userData.user) {
      const { data } = await supabase.auth.getSession();
      return data.session;
    }

    // 서버 검증 실패 → 토큰 갱신을 한 번 더 시도
    const { data: refreshed, error: refreshError } =
      await supabase.auth.refreshSession();
    if (!refreshError && refreshed.session) {
      return refreshed.session;
    }

    // 여기까지 왔으면 진짜로 유효한 세션이 없는 상태야. 이제 정리해.
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    return null;
  } catch {
    // 예상치 못한 예외에서도 곧바로 로그아웃시키지 않고, 마지막으로
    // 갱신을 한 번 더 시도해봐.
    try {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) return refreshed.session;
    } catch {
      /* noop */
    }
    return null;
  }
}

export type ProfileStatus = 'pending' | 'approved' | 'rejected';

export type ProfileRole = 'operator' | 'owner' | 'teacher';

export type Profile = {
  id: string;
  full_name: string | null;
  role: ProfileRole;
  phone: string | null;
  status: ProfileStatus;
  academy_id: string | null;
  academy_name: string | null;
  address: string | null;
  created_at: string;
};

export type Academy = {
  id: string;
  name: string;
  address: string | null;
  owner_id: string | null;
  created_at: string;
};